import * as gqlTypes from "@hydrologiq/gql/graphql"

import { useQuery } from "@apollo/client"
import { Box, HStack, Table, Text } from "@chakra-ui/react"
import StatusBadge from "@hydrologiq/shared/asset/status/StatusBadge"
import { toUserTZ } from "@hydrologiq/common/helpers/date"
import Location from "@hydrologiq/shared/attributes/Location"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"

interface AssetStatusHistoryProps {
  asset: gqlTypes.Asset
}

function AssetStatusHistory(props: AssetStatusHistoryProps) {
  const { asset } = props

  const { data, loading } = useQuery(gqlTypes.GetAssetStatusHistoryDocument, {
    variables: {
      asset: asset.id,
    },
  })
  const sitesFeatureEnabled = useGetFeature("site_reservation")
  const showCustomerSite = sitesFeatureEnabled

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.TIMESTAMP]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.TIMESTAMP],
    [COPY_KEYS.HEADINGS.STATUS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.STATUS],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    [COPY_KEYS.HEADINGS.AMOUNT]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.AMOUNT],
    [COPY_KEYS.HEADINGS.NOTE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.NOTE],
  })

  return (
    <Box mt={4} overflowX="auto">
      <Table.Root variant="hydrologiq">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>
              <HStack>
                <Text>{copy[COPY_KEYS.HEADINGS.TIMESTAMP]}</Text>
              </HStack>
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              <HStack>
                <Text>{copy[COPY_KEYS.HEADINGS.STATUS]}</Text>
              </HStack>
            </Table.ColumnHeader>
            {showCustomerSite && (
              <Table.ColumnHeader>
                <HStack>
                  <Text>{copy[COPY_KEYS.HEADINGS.SITE]}</Text>
                </HStack>
              </Table.ColumnHeader>
            )}
            <Table.ColumnHeader>
              <HStack>
                <Text>{copy[COPY_KEYS.HEADINGS.AMOUNT]}</Text>
              </HStack>
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              <HStack>
                <Text>{copy[COPY_KEYS.HEADINGS.NOTE]}</Text>
              </HStack>
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {loading
            ? [...Array(10)].map((_, index) => (
                <Table.Row key={index}>
                  <Table.Cell colSpan={5}></Table.Cell>
                </Table.Row>
              ))
            : data?.assetStatusHistory?.map((status) => (
                <Table.Row key={status.id}>
                  <Table.Cell minW="100px" maxW="125px">
                    {toUserTZ(status.timestamp).format("yyyy-MM-dd HH:mm")}
                  </Table.Cell>
                  <Table.Cell minW="200px" maxW="300px">
                    <HStack gap={2}>
                      {/* <StatusBadge edit={false} asset={asset} status={status.status_previous} />
                      <Text fontWeight="bold" fontSize="18px">
                        &gt;
                      </Text> */}
                      <StatusBadge edit={false} asset={asset} status={status.status} />
                    </HStack>
                  </Table.Cell>
                  {showCustomerSite && (
                    <Table.Cell minW="25px" maxW="1200px">
                      {status.customer_site ? (
                        <Location name={status.customer_site.name} location={status.customer_site.location} />
                      ) : (
                        "Unreserved"
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell minW="40px" maxW="100px">
                    {status.fuel_level || "No information provided"}
                  </Table.Cell>
                  <Table.Cell minW="250px">{status.note || "No information provided"}</Table.Cell>
                </Table.Row>
              ))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}

export default AssetStatusHistory
